/**
 * storage.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";

import { identity } from "../utilities/functions";

import { InMemoryStorage } from "./in-memory-storage";

export interface WrappedStorage<T>
{
  created : Date;
  modified : Date;
  modifyCount : number;
  version : number;
  key : string;
  content : T;
  fresh? : boolean;
}

export interface WrappedStorageJSON<U>
{
  created : number;
  modified : number;
  modifyCount : number;
  version : number;
  key : string;
  content : U;
}

/**
 * The current storage protocol version, used for migrating and overwriting
 * types of stored values.
 * @devConfig
 */
const STORAGE_PROTOCOL_VERSION : number = 0;

/**
 * The {@link StorageService} provides a wrapper around persistent browser
 * storage APIs (generally LocalStorage) that supports automatic parsing and
 * JSONification of general JS objects, as well as other convenience methods.
 */
@Injectable()
export class StorageService
{
  /**
   * The driving implementation of the Storage API to rely on.
   * @type {Storage}
   */
  private driver : Storage;

  /**
   * The current storage service "protocol" version. The version number is
   * useful for forcing resets of values that haven't been updated since a
   * format change.
   */
  private version : number = STORAGE_PROTOCOL_VERSION;

  /**
   * Test method to determine if we can use LocalStorage as our implementation
   * or if we should bail to our in-memory polyfill.
   * @return {boolean}
   */
  private supportsLS () : boolean
  {
    // This code borrowed from Modernizr. See their test rig for details on why
    // going "aggressive" (for a try-catch) is necessary for detection.
    try {
      const x : string = "support_test";
      localStorage.setItem(x, x);
      localStorage.removeItem(x);
      return true;
    }
    catch (e)
    {
      // TODO: present alert
      return false;
    }
  }

  /**
   * Create the {@link StorageService}
   * @return {StorageService}
   */
  constructor ()
  {
    this.driver = this.supportsLS() ? localStorage : new InMemoryStorage();
  }

  /**
   * Retrieve a storage entry, or if null, the fallback value provided
   * @param  {string} name [description]
   * @param  {any =    null}        fallback [description]
   * @return {any}
   */
  public get (name : string, fallback : any = null) : any
  {
    try {
      const retrieved : any = JSON.parse(this.driver.getItem(name));
      return (retrieved === null) ? fallback : retrieved;
    } catch (err) {
      console.error(err);
      return;
    }
  }

  /**
   * Save an entry to storage
   * @param {string} name  [description]
   * @param {any}    value [description]
   */
  public set (name : string, value : any) : void
  {
    this.driver.setItem(name, JSON.stringify(value));
  }

  /**
   * Remove an entry, if it exists
   * @param {string} name [description]
   */
  public remove (name : string) : void
  {
    this.driver.removeItem(name);
  }

  /**
   * Remove all entries (for this domain)
   */
  public clear () : void
  {
    this.driver.clear();
  }

  // ==================== NEW WRAPPED ENTRY API ====================

  /**
   * Store a value with a metadata wrapper
   * @param name The key to store the entry under
   * @param value The value to store at this key
   * @param compressor Optional callback to convert the value to a more
   *                   JSON-consumable type
   */
  public setWrapped<T, U> (
    name : string,
    value : T,
    compressor? : (value : T) => U
  ) : void
  {
    const existing : WrappedStorage<T> = this.getWrappedRaw<T, U>(name);
    // Set the new value
    existing.content = value;
    // Indicate we saved the value with the current storage protocol version
    existing.version = this.version;
    // Increment the number of modifications made
    existing.modifyCount += 1;
    // Record the timestamp of the modification
    existing.modified = new Date();
    this.set(name, this.serializeWrapper(existing, compressor));
  }

  /**
   * Retrieve a stored value WITH its storage metadata
   * @param name The key to load
   * @param fallback Optional fallback value if the stored one isn't found
   * @param extractor Callback to extract the value from its JSON type
   */
  public getWrappedRaw<T, U> (
    name : string,
    fallback : T = null,
    extractor? : (value : U) => T
  ) : WrappedStorage<T>
  {
    let loaded : WrappedStorageJSON<U> = this.get(name);
    if (loaded === null || typeof loaded !== "object")
    {
      return this.wrappedFallback(name, fallback);
    }
    else
    {
      return this.deserializeWrapper(loaded, extractor);
    }
  }

  /**
   * Retrieve a stored value directly (without the additional metadata)
   * @param name The key to load
   * @param fallback Optional fallback value if the stored one isn't found
   * @param extractor Callback to extract the value from its JSON type
   */
  public getWrappedValue<T, U> (
    name : string,
    fallback : T = null,
    extractor? : (value : U) => T
  ) : T
  {
    return this.getWrappedRaw(name, fallback, extractor).content;
  }

  /**
   * Internal function to create a metadata wrapper for a new entry
   * @param name The key the entry is for
   * @param value The initial value to set (null if absent)
   */
  private wrappedFallback<T> (name : string, value : T = null)
    : WrappedStorage<T>
  {
    const now : Date = new Date();
    return {
      created: now,
      modified: now,
      key: name,
      content: value,
      modifyCount: 0,
      version: this.version,
      fresh: true
    };
  }

  /**
   * Internal function to convert the parsed JSON into hydrated storage metadata
   * @param stored The wrapped stored value as parsed from JSON
   * @param extractor A callback to operate on the content field
   */
  private deserializeWrapper<T, U> (
    stored : WrappedStorageJSON<U>,
    extractor? : (value : U) => T
  ) : WrappedStorage<T>
  {
    const content : T = (extractor
      ? extractor(stored.content)
      : (stored.content as any as T));
    return {
      created: new Date(stored.created),
      modified: new Date(stored.modified),
      key: stored.key,
      modifyCount: stored.modifyCount,
      version: stored.version,
      content
    };
  }

  /**
   * Prepare a storage wrapper for conversion to JSON
   * @param toSet The wrapped value for writing
   * @param compressor A callback to operate on the content field
   */
  private serializeWrapper<T, U> (
    toSet : WrappedStorage<T>,
    compressor? : (value : T) => U
  ) : WrappedStorageJSON<U>
  {
    const content : U = (compressor
      ? compressor(toSet.content)
      : (toSet.content as any as U));
    return {
      created: toSet.created.valueOf(),
      modified: toSet.modified.valueOf(),
      key: toSet.key,
      modifyCount: toSet.modifyCount,
      version: toSet.version,
      content
    };
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/APIs/storage.service.ts